/**
 * NotificationCenter Component
 *
 * Main notification panel with popover UI.
 * Includes filters, notification list, and bulk actions.
 */

import { Trash2, CheckCheck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useNotificationContext } from '@/context/NotificationContext';
import { NotificationBadge } from './NotificationBadge';
import { NotificationFilters } from './NotificationFilters';
import { NotificationList } from './NotificationList';

export function NotificationCenter() {
  const {
    isOpen,
    setIsOpen,
    unreadCount,
    notifications,
    markAllAsRead,
    dismissAll,
    filter,
    systemNotifications,
    systemNotificationCount,
    clearSystemNotifications,
  } = useNotificationContext();

  const hasNotifications = notifications.length > 0;
  const hasUnread = unreadCount > 0;
  const isSystemFilter = filter === 'system';
  const hasSystemNotifications = systemNotificationCount > 0;

  // Show footer when there's content to act on
  const showFooter = isSystemFilter ? hasSystemNotifications : hasNotifications;

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const handleDismissAll = async () => {
    await dismissAll();
  };

  const handleClearSystem = () => {
    clearSystemNotifications();
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div>
          <NotificationBadge />
        </div>
      </PopoverTrigger>

      <PopoverContent
        className="w-[380px] p-0"
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="px-4 pt-4 pb-3 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold">Notifications</h3>
              {(hasUnread || hasSystemNotifications) && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                  {(unreadCount + systemNotificationCount) > 99 ? '99+' : unreadCount + systemNotificationCount}
                </span>
              )}
            </div>
          </div>

          {/* Filter tabs */}
          <NotificationFilters />
        </div>

        {/* Content */}
        <div className="max-h-[350px] overflow-y-auto px-3 py-2">
          <NotificationList />
        </div>

        {/* Footer with actions */}
        {showFooter && (
          <>
            <Separator />
            <div className="px-4 py-2 flex items-center justify-between bg-muted/30">
              {isSystemFilter ? (
                // System notifications - only show clear button
                <>
                  <span className="text-xs text-muted-foreground">
                    {systemNotificationCount} message{systemNotificationCount !== 1 ? 's' : ''}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground hover:text-destructive"
                    onClick={handleClearSystem}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    Clear all
                  </Button>
                </>
              ) : (
                // DB notifications - show mark read and clear buttons
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground hover:text-foreground"
                    onClick={handleMarkAllRead}
                    disabled={!hasUnread}
                  >
                    <CheckCheck className="h-3.5 w-3.5 mr-1.5" />
                    Mark all read
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground hover:text-destructive"
                    onClick={handleDismissAll}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    Clear all
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
